import styles from "../../../../styles/finder/finder.module.scss"
import StyledText from "../../../typography/typography-component"
import skColors from "../../../../styles/skColors"
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslations } from "next-intl"
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup, useMapContext } from "react-simple-maps"

type DoctorMapOverviewItem = {
  doctorsName: string
  officeName: string
  address: string
  state: string
  lat: number
  lon: number
  webAddress: string
  distanceKm: number
}

type MarkerCluster = {
  key: string
  x: number
  y: number
  doctors: DoctorMapOverviewItem[]
}

const GEO_URL = "/maps/germany.geo.json"

const DEFAULT_CENTER: [number, number] = [10.5, 51.1]
const MIN_ZOOM = 1
const MAX_ZOOM = 12

// ComposableMap renders with viewBox "0 0 800 600" and the SVG is scaled to
// fit its container. Marker radii are in viewBox units, so the on-screen
// size shrinks with the container. On desktop the card content is
// 760px - 2*24px padding = 712px wide; that is the size the markers were
// tuned for, so we treat it as the reference and only scale up below it.
const DESKTOP_SVG_WIDTH = 712

// Outer marker radius in (scaled) viewBox units. Two markers whose centers
// are closer than one outer diameter on screen are merged into a cluster.
const MARKER_OUTER_RADIUS = 12

const doctorKey = (doctor: DoctorMapOverviewItem) => `${doctor.doctorsName}-${doctor.officeName}`

/**
 * Renders the doctor markers. Lives inside ComposableMap so it can use the
 * map projection to group practices that would overlap at the current zoom
 * (e.g. several practices at the same address in Bremen).
 */
const DoctorMarkers = ({
  doctors,
  markerScale,
  selectedKeys,
  onSelect,
}: {
  doctors: DoctorMapOverviewItem[]
  markerScale: number
  selectedKeys: Set<string>
  onSelect: (cluster: MarkerCluster) => void
}) => {
  const { projection } = useMapContext()

  const clusters = useMemo<MarkerCluster[]>(() => {
    // Markers are drawn with transform scale(markerScale), so their on-map
    // radius in unscaled viewBox units is MARKER_OUTER_RADIUS * markerScale.
    const mergeDistance = MARKER_OUTER_RADIUS * 2 * markerScale
    const result: MarkerCluster[] = []

    for (const doctor of doctors) {
      const projected = projection([doctor.lon, doctor.lat])
      if (!projected) continue
      const [x, y] = projected

      const existing = result.find((cluster) => Math.hypot(cluster.x - x, cluster.y - y) < mergeDistance)
      if (existing) {
        existing.doctors.push(doctor)
      } else {
        result.push({ key: doctorKey(doctor), x, y, doctors: [doctor] })
      }
    }

    return result
  }, [doctors, markerScale, projection])

  return (
    <>
      {clusters.map((cluster) => {
        const isCluster = cluster.doctors.length > 1
        const isSelected = cluster.doctors.some((doctor) => selectedKeys.has(doctorKey(doctor)))
        const label = isCluster
          ? `${cluster.doctors.length} × ${cluster.doctors[0].address}`
          : `${cluster.doctors[0].officeName} — ${cluster.doctors[0].address}`

        return (
          <Marker key={cluster.key} coordinates={[cluster.doctors[0].lon, cluster.doctors[0].lat]}>
            <g
              role="button"
              tabIndex={0}
              aria-label={label}
              onClick={() => onSelect(cluster)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  onSelect(cluster)
                }
              }}
              style={{ cursor: "pointer", outline: "none" }}
              transform={`scale(${markerScale})`}
            >
              <title>{label}</title>
              <circle
                r={MARKER_OUTER_RADIUS}
                fill={isSelected ? "rgba(31, 158, 203, 0.35)" : "rgba(31, 158, 203, 0.18)"}
              />
              {isCluster ? (
                <>
                  <circle
                    r={9}
                    fill="var(--turquoise-500)"
                    stroke={isSelected ? "var(--darkgreen-700)" : "white"}
                    strokeWidth="2"
                  />
                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="10"
                    fontWeight="700"
                    fill="white"
                    style={{ pointerEvents: "none", userSelect: "none" }}
                  >
                    {cluster.doctors.length}
                  </text>
                </>
              ) : (
                <circle
                  r={6}
                  fill="var(--turquoise-500)"
                  stroke={isSelected ? "var(--darkgreen-700)" : "white"}
                  strokeWidth="2"
                />
              )}
            </g>
          </Marker>
        )
      })}
    </>
  )
}

const DoctorMapOverview = ({ doctors }: { doctors: DoctorMapOverviewItem[] }) => {
  const t = useTranslations("index.finder")

  const [position, setPosition] = useState<{ coordinates: [number, number]; zoom: number }>({
    coordinates: DEFAULT_CENTER,
    zoom: 1,
  })

  // Practices the user tapped on the map. Shown in a panel with name,
  // address and website link instead of opening a new tab straight away.
  const [selected, setSelected] = useState<DoctorMapOverviewItem[] | null>(null)
  const selectedKeys = useMemo(() => new Set((selected ?? []).map(doctorKey)), [selected])

  // Drop the selection when the doctor list changes (e.g. new filter).
  useEffect(() => {
    setSelected(null)
  }, [doctors])

  // The popover is portaled to document.body so it can sit fixed at the
  // bottom of the viewport; only render it once mounted on the client.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  const clampZoom = (zoom: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom))

  const zoomIn = () => setPosition((prev) => ({ ...prev, zoom: clampZoom(prev.zoom * 1.6) }))
  const zoomOut = () => setPosition((prev) => ({ ...prev, zoom: clampZoom(prev.zoom / 1.6) }))
  const resetZoom = () => setPosition({ coordinates: DEFAULT_CENTER, zoom: 1 })

  // Track the rendered SVG width so markers can be enlarged on narrow
  // screens, where the viewBox is scaled down and 12 units become ~4px.
  const svgRef = useRef<SVGSVGElement>(null)
  const [svgWidth, setSvgWidth] = useState(DESKTOP_SVG_WIDTH)

  useEffect(() => {
    const svg = svgRef.current
    if (!svg || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width
      if (width && width > 0) setSvgWidth(width)
    })
    observer.observe(svg)
    return () => observer.disconnect()
  }, [])

  // Compensate for the SVG being rendered narrower than the desktop
  // reference width. Never scale below 1, so desktop rendering is unchanged.
  const viewportScale = Math.max(1, DESKTOP_SVG_WIDTH / svgWidth)

  // Keep markers a constant on-screen size as the group scales, so that
  // closely-spaced points actually separate (and stay clickable) on zoom.
  const markerScale = viewportScale / position.zoom

  return (
    <div className={styles.doctorMapPanel}>
      <StyledText variant="h5" color={skColors.darkgreen[600]}>
        {t("step3b.map.subtitle-new", { count: doctors.length })}
      </StyledText>

      <div className={styles.doctorMapCard}>
        <div className={styles.doctorMapControls}>
          <button
            type="button"
            className={styles.doctorMapControlButton}
            onClick={zoomIn}
            disabled={position.zoom >= MAX_ZOOM}
            aria-label={t("step3b.map.zoom-in")}
          >
            +
          </button>
          <button
            type="button"
            className={styles.doctorMapControlButton}
            onClick={zoomOut}
            disabled={position.zoom <= MIN_ZOOM}
            aria-label={t("step3b.map.zoom-out")}
          >
            −
          </button>
          <button
            type="button"
            className={styles.doctorMapControlButton}
            onClick={resetZoom}
            disabled={position.zoom === 1 && position.coordinates === DEFAULT_CENTER}
            aria-label={t("step3b.map.zoom-reset")}
          >
            ⟳
          </button>
        </div>

        <ComposableMap
          ref={svgRef}
          projection="geoMercator"
          projectionConfig={{ center: DEFAULT_CENTER, scale: 3000 }}
          className={styles.doctorMapSvg}
          aria-label={t("step3b.map.title")}
        >
          <ZoomableGroup
            center={position.coordinates}
            zoom={position.zoom}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            onMoveEnd={({ coordinates, zoom }) =>
              setPosition({
                coordinates: coordinates ?? DEFAULT_CENTER,
                zoom: zoom ?? 1,
              })
            }
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="rgba(88, 180, 190, 0.12)"
                    stroke="rgba(18, 96, 110, 0.8)"
                    strokeWidth={1.5}
                    style={{ outline: "none" }}
                  />
                ))
              }
            </Geographies>

            <DoctorMarkers
              doctors={doctors}
              markerScale={markerScale}
              selectedKeys={selectedKeys}
              onSelect={(cluster) => setSelected(cluster.doctors)}
            />
          </ZoomableGroup>
        </ComposableMap>

        {mounted &&
          selected &&
          selected.length > 0 &&
          createPortal(
            <div className={styles.doctorMapPopover} role="dialog" aria-label={t("step3b.map.selected-title")}>
              <div className={styles.doctorMapPopoverHeader}>
                <StyledText as="span" variant="pRegular" color={skColors.darkgreen[600]}>
                  {selected.length === 1
                    ? t("step3b.map.selected-one")
                    : t("step3b.map.selected-count", { count: selected.length })}
                </StyledText>
                <button
                  type="button"
                  className={styles.doctorMapControlButton}
                  onClick={() => setSelected(null)}
                  aria-label={t("step3b.map.close")}
                >
                  ×
                </button>
              </div>
              <ul className={styles.doctorMapPopoverList}>
                {selected.map((doctor) => (
                  <li key={doctorKey(doctor)} className={styles.doctorMapPopoverItem}>
                    <div className={styles.doctorMapPopoverText}>
                      <StyledText as="span" variant="pRegular" color={skColors.darkgreen[700]}>
                        {doctor.officeName}
                      </StyledText>
                      {doctor.doctorsName && doctor.doctorsName !== doctor.officeName && (
                        <StyledText as="span" variant="pSmall" color={skColors.darkgreen[500]}>
                          {doctor.doctorsName}
                        </StyledText>
                      )}
                      <StyledText as="span" variant="pSmall" color={skColors.darkgreen[500]}>
                        {doctor.address}
                      </StyledText>
                    </div>
                    {doctor.webAddress && (
                      <a
                        className={styles.doctorMapPopoverLink}
                        href={doctor.webAddress}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <StyledText variant={"cta"} color={"white"}>
                          {t("step3b.map.open-website")}
                        </StyledText>
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>,
            document.body
          )}
      </div>
    </div>
  )
}

export default DoctorMapOverview
